import { useEffect, useState } from "react"

import { RESOURCES_NAMES } from "@/const/resource.constants"
import {
  Config,
  Params,
  useCreate,
  useDelete,
  useHttp,
  usePatch
} from "@/hooks/useHttp"
import {
  getGitHubIssuesSettingsData,
  getIntegrationDetailsData,
  getIntegrationsHealthData,
  getIntegrationsListData,
  getPreInstallOptionsECR,
  getTicketingSystemsData
} from "./useIntegrations.selector"

import type { QueryClient } from "@tanstack/react-query"
import type {
  AcrPreInstallResponse,
  AsanaPreInstallRequest,
  AsanaPreInstallResponse,
  DownloadKodemCLIResponse,
  GcrPreInstallResponse,
  GithubPreInstallIntegrationResponse,
  IntegrationDetailsResponse,
  IntegrationsListResponse,
  PackageRegistryEntryInput,
  SlackTestTarget,
  TestPackageRegistryResponse,
  TestWebhookResponse
} from "./useIntegrations.types"

export const useGetIntegrationsList = <
  T extends IntegrationsListResponse = IntegrationsListResponse
>(
  params: Params = {},
  config: Config<T, ReturnType<typeof getIntegrationsListData>> = {}
) => {
  const context = useHttp<T, ReturnType<typeof getIntegrationsListData>>(
    RESOURCES_NAMES.INTEGRATIONS.LIST,
    params,
    { select: getIntegrationsListData, ...config }
  )

  return context
}

// The ticketing systems the tenant connected, from the integrations list
export const useGetTicketingSystems = () =>
  useHttp<IntegrationsListResponse, ReturnType<typeof getTicketingSystemsData>>(
    RESOURCES_NAMES.INTEGRATIONS.LIST,
    {},
    { select: getTicketingSystemsData }
  )

const INCLUDE_HEALTH_PARAMS = { includeHealth: true }

// After this, the list stops waiting for health and shows the connected icon
const HEALTH_WAIT_MS = 5000

export const useGetIntegrationsHealth = () => {
  const context = useHttp<
    IntegrationsListResponse,
    ReturnType<typeof getIntegrationsHealthData>
  >(RESOURCES_NAMES.INTEGRATIONS.LIST, INCLUDE_HEALTH_PARAMS, {
    select: getIntegrationsHealthData,
    // a retry re-checks every vendor; show the connected icon instead
    retry: false
  })
  const [waitedTooLong, setWaitedTooLong] = useState(false)

  useEffect(() => {
    const timer = setTimeout(() => setWaitedTooLong(true), HEALTH_WAIT_MS)

    return () => clearTimeout(timer)
  }, [])

  return { ...context, isHealthLoading: context.isLoading && !waitedTooLong }
}

export const useGetIntegrationDetails = <
  T extends IntegrationDetailsResponse = IntegrationDetailsResponse
>(
  name: string,
  params: Params = {},
  config: Config<T, ReturnType<typeof getIntegrationDetailsData>> = {}
) => {
  const context = useHttp<T, ReturnType<typeof getIntegrationDetailsData>>(
    `${RESOURCES_NAMES.INTEGRATIONS.LIST}/${name}`,
    params,
    { select: getIntegrationDetailsData, ...config }
  )

  return context
}

export const useGetGitHubIssuesSettings = (enabled: boolean) =>
  useHttp<
    IntegrationDetailsResponse,
    ReturnType<typeof getGitHubIssuesSettingsData>
  >(
    `${RESOURCES_NAMES.INTEGRATIONS.LIST}/github`,
    {},
    // read app-wide by the ticketing provider: a failure hides GitHub without a toast
    { select: getGitHubIssuesSettingsData, enabled, silentErrors: true }
  )

// Workflow actions list a vendor's installations (webhook endpoints, Slack channels) and
// are offered only once it is installed.
const invalidateWorkflowActions = (client: QueryClient) => {
  const { ACTIONS } = RESOURCES_NAMES.SETTINGS.WORKFLOWS
  return Promise.all([
    client.invalidateQueries({ queryKey: [ACTIONS] }),
    client.invalidateQueries({ queryKey: [`${ACTIONS}/options`] })
  ])
}

// The list shows each integration's connection and health; prefix-matches the plain list
// and the includeHealth request, not the per-integration pages
const invalidateIntegrationsList = (client: QueryClient) =>
  client.invalidateQueries({ queryKey: [RESOURCES_NAMES.INTEGRATIONS.LIST] })

export const usePatchIntegration = (params: Params<{ vendor: string }>) => {
  const updater = usePatch(
    `${RESOURCES_NAMES.INTEGRATIONS.LIST}/${params.vendor}`,
    undefined,
    undefined,
    (client) => ({ onSuccess: () => invalidateWorkflowActions(client) })
  )

  return updater
}

export const useDeleteIntegration = <T, S = T>(
  params: Params<{ vendor: string }>
) => {
  const remover = useDelete<T, S>(
    `${RESOURCES_NAMES.INTEGRATIONS.LIST}/${params.vendor}`,
    params,
    undefined,
    (client) => ({
      onSuccess: () =>
        Promise.all([
          invalidateIntegrationsList(client),
          invalidateWorkflowActions(client)
        ])
    })
  )

  return remover
}

export const useGetCallbackIntegrations = (
  vendor: string,
  params: Params<Record<string, string>>,
  config: Config
) => {
  const context = useHttp(
    `${RESOURCES_NAMES.INTEGRATIONS.CALLBACK}/${vendor}`,
    params,
    config
  )

  return context
}

export type InstallStatus = {
  status: string
  installedRegistriesUrls: string[]
}

export type InstallRegistryResponse = { data: InstallStatus }

export const useInstallIntegration = (params: Params<{ vendor: string }>) => {
  const updater = useCreate(
    `${RESOURCES_NAMES.INTEGRATIONS.LIST}/${params.vendor}`,
    undefined,
    undefined,
    (client) => ({
      onSuccess() {
        return Promise.all([
          client.invalidateQueries({
            queryKey: [`${RESOURCES_NAMES.INTEGRATIONS.LIST}/${params.vendor}`]
          }),
          invalidateIntegrationsList(client),
          invalidateWorkflowActions(client)
        ])
      }
    })
  )

  return updater
}

export const useNotifyAdminsOnCallbackIntegrations = <T = unknown, S = T>() => {
  const context = useCreate<T, S>(
    `${RESOURCES_NAMES.INTEGRATIONS.NOTIFY_CALLBACK}`
  )

  return context
}

export const usePreInstallECR = (vendor?: string) => {
  const context = useHttp(
    RESOURCES_NAMES.INTEGRATIONS.ECR.PRE_INSTALL,
    undefined,
    { enabled: vendor === "ecr", select: getPreInstallOptionsECR }
  )

  return context
}

// Creates the tenant's service account on first use; needs integrations write permission.
export const usePreInstallGcr = (options?: { enabled?: boolean }) =>
  useHttp<GcrPreInstallResponse>(
    RESOURCES_NAMES.INTEGRATIONS.GCR.PRE_INSTALL,
    undefined,
    { enabled: options?.enabled ?? true, throwOnError: false }
  )

// Creates the tenant's service account on first use.
export const usePreInstallAcr = () =>
  useHttp<AcrPreInstallResponse>(RESOURCES_NAMES.INTEGRATIONS.ACR.PRE_INSTALL)

// Each call issues a single-use OAuth state, so it is a mutation: never cached or retried
export const usePreInstallAsana = () =>
  useCreate<AsanaPreInstallResponse, AsanaPreInstallRequest | void>(
    RESOURCES_NAMES.INTEGRATIONS.ASANA.PRE_INSTALL
  )

// Manual-trigger pre-install for the GitHub Custom App flow. Response
// carries manifestJson + formAction, POSTed to GitHub via submitManifestToGitHub.
export const usePreInstallGithub = (params?: {
  org: string | undefined
  githubHost: string | undefined
}) => {
  const queryParams = Object.fromEntries(
    Object.entries(params ?? {}).filter(([, v]) => v !== undefined)
  )

  return useHttp<GithubPreInstallIntegrationResponse>(
    RESOURCES_NAMES.INTEGRATIONS.GITHUB.PRE_INSTALL,
    queryParams,
    { enabled: false }
  )
}

export const useTestWebhook = <
  T = TestWebhookResponse,
  S = { webhook: { url: string; secret?: string } }
>() => useCreate<T, S>(`${RESOURCES_NAMES.INTEGRATIONS.LIST}/webhooks/test`)

// Sent is the only success; Slack's refusals come back as errors with a displayMessage.
export const useTestSlackChannel = () =>
  useCreate<KosmosApi.Response<true>, SlackTestTarget>(
    `${RESOURCES_NAMES.INTEGRATIONS.LIST}/slack/test`
  )

export const useTestPackageRegistry = <
  T = TestPackageRegistryResponse,
  S = { packageRegistry: PackageRegistryEntryInput }
>() =>
  useCreate<T, S>(`${RESOURCES_NAMES.INTEGRATIONS.LIST}/package_registry/test`)

export const useDownloadIntegration = (vendor: string, platform: string) => {
  const context = useHttp<DownloadKodemCLIResponse>(
    `${RESOURCES_NAMES.INTEGRATIONS.DOWNLOAD}/${vendor}`,
    { platform },
    { enabled: false, staleTime: Infinity }
  )
  return context
}
